import type { Movement } from "./types";

export interface ExpenseSuggestion {
  sourceId: string;
  merchant: string;
  spentOn: string;
  category?: string;
  paymentMethod?: string;
  budgetImpact?: boolean;
  cardFundingMode?: "now" | "later";
  savings?: { enabled: false } | { enabled: true; percent: number };
}

export function expenseNameKey(name: string): string {
  return name.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase("es-MX")
    .replace(/[^\p{L}\p{N}]+/gu, " ").trim().replace(/\s+/g, " ");
}

/** Read-only hints from one recorded purchase, never a copy of its financial state. */
export function suggestExpense(
  name: string, movements: readonly Movement[],
  catalog: { categories: readonly string[]; paymentMethods: readonly string[]; today: string },
): ExpenseSuggestion | undefined {
  const key = expenseNameKey(name);
  if (key.length < 2) return;
  const source = movements.filter((item) => item.kind === "expense" && item.syncState === "synced"
    && expenseNameKey(item.merchant) === key
    && (item.spentOn ?? item.createdAt.slice(0, 10)) <= catalog.today
    && (!item.installmentPurchaseId || item.installmentNumber === 1)
    && (!item.installmentCount || item.installmentNumber === 1))
    .sort((a, b) => (b.spentOn ?? b.createdAt.slice(0, 10)).localeCompare(a.spentOn ?? a.createdAt.slice(0, 10))
      || b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id))[0];
  if (!source) return;
  const suggestion: ExpenseSuggestion = {
    sourceId: source.id, merchant: source.merchant,
    spentOn: source.spentOn ?? source.createdAt.slice(0, 10),
    category: source.category && catalog.categories.includes(source.category) ? source.category : undefined,
    paymentMethod: catalog.paymentMethods.includes(source.paymentMethod) ? source.paymentMethod : undefined,
    budgetImpact: typeof source.budgetImpact === "boolean" ? source.budgetImpact : undefined,
  };
  // Partial funding, MSI and old coverage do not establish how to fund a new purchase.
  if (!source.installmentCount && (source.cardFundingMode === "now" || source.cardFundingMode === "later")) {
    suggestion.cardFundingMode = source.cardFundingMode;
  }
  const saving = source.savingsContributionCents;
  const billed = source.billedAmountCents ?? source.amountCents;
  const reimbursement = source.reimbursementCents ?? 0;
  const own = billed - reimbursement;
  if (saving === 0) suggestion.savings = { enabled: false };
  else if (!source.installmentCount && source.budgetImpact === true && Number.isSafeInteger(saving)
    && saving! > 0 && Number.isSafeInteger(own) && own > 0) {
    // The history stores an amount, not the original percentage/manual choice.
    // Only reuse a standard rate that exactly reproduces those integer cents.
    const rates = [1, 5, 10].filter((rate) => Math.round(own * rate / 100) === saving);
    if (rates.length === 1) suggestion.savings = { enabled: true, percent: rates[0] };
  }
  return suggestion;
}
